/**
 * Mock data for JobSalary feature in demo mode
 *
 * This file provides mock watchlist data that simulates the real NATS data
 * without requiring authentication. Used exclusively in demo mode.
 */
import type { WatchlistDto, WatchlistGroup, WatchlistJob } from "./JobSalary.dto";
import { JobSalary } from "./JobSalary.model";
import type { MetroArea, Quantile } from "./watchlist.filters";

// Mock Metro Areas (matching real data structure)
export const MOCK_METRO_AREAS: MetroArea[] = [
  { id: "16980", title: "Chicago-Naperville-Elgin, IL-IN" },
  { id: "35620", title: "New York-Newark-Jersey City, NY-NJ-PA" },
  { id: "41860", title: "San Francisco-Oakland-Hayward, CA" },
  { id: "31080", title: "Los Angeles-Long Beach-Anaheim, CA" },
  { id: "19100", title: "Dallas-Fort Worth-Arlington, TX" },
  { id: "12060", title: "Atlanta-Sandy Springs-Roswell, GA" },
  { id: "14460", title: "Boston-Cambridge-Newton, MA-NH" },
  { id: "47900", title: "Washington-Arlington-Alexandria, DC-VA-MD-WV" },
  { id: "42660", title: "Seattle-Tacoma-Bellevue, WA" },
  { id: "26420", title: "Houston-The Woodlands-Sugar Land, TX" },
];

// Mock Quantiles (experience levels)
export const MOCK_QUANTILES: Quantile[] = [
  { id: "a_pct10", short: "<1y", title: "<1y exp" },
  { id: "a_pct25", short: "1-5y", title: "1-5y exp" },
  { id: "a_median", short: "5-10y", title: "5-10y exp" },
  { id: "a_pct75", short: "10-15y", title: "10-15y exp" },
  { id: "a_pct90", short: ">15y", title: ">15y exp" },
];

// Mock job salary data (based on screenshot)
interface MockJobSalaryData {
  jobTitle: string;
  jobSubject: string;
  salary: number;
  netChange: number;
  netChangeOneYear: number;
  netChangeThreeYear: number;
  netChangeFiveYear: number;
  liquidity: number;
  averageTenureInYears: number;
  growthRate: number;
}

export const UNKNOWN_JOB_SALARY: MockJobSalaryData = {
  jobTitle: "Unknown TItle",
  jobSubject: "unknown-title",
  salary: 99_999,
  netChange: -9,
  netChangeOneYear: 999,
  netChangeThreeYear: 9_999,
  netChangeFiveYear: 99_999,
  liquidity: 999,
  averageTenureInYears: 9.9,
  growthRate: 0.009,
};

export const MOCK_JOB_SALARIES: MockJobSalaryData[] = [
  {
    jobTitle: "Accountant Supervisor",
    jobSubject: "Accountant-Supervisor",
    salary: 150919,
    netChange: -8,
    netChangeOneYear: 5200,
    netChangeThreeYear: 12000,
    netChangeFiveYear: 18500,
    liquidity: 850,
    averageTenureInYears: 4.2,
    growthRate: 0.035,
  },
  {
    jobTitle: "Actuary Clerk",
    jobSubject: "Actuary-Clerk",
    salary: 48749,
    netChange: -3,
    netChangeOneYear: 2100,
    netChangeThreeYear: 4800,
    netChangeFiveYear: 6200,
    liquidity: 320,
    averageTenureInYears: 3.1,
    growthRate: 0.018,
  },
  {
    jobTitle: "Auditor Appraiser",
    jobSubject: "Auditor-Appraiser",
    salary: 99074,
    netChange: -5,
    netChangeOneYear: 4500,
    netChangeThreeYear: 9200,
    netChangeFiveYear: 14100,
    liquidity: 590,
    averageTenureInYears: 3.8,
    growthRate: 0.028,
  },
  {
    jobTitle: "Banker",
    jobSubject: "Banker",
    salary: 76987,
    netChange: 4,
    netChangeOneYear: 3800,
    netChangeThreeYear: 7500,
    netChangeFiveYear: 11200,
    liquidity: 480,
    averageTenureInYears: 4.5,
    growthRate: 0.022,
  },
  {
    jobTitle: "Bookkeeping Assistant",
    jobSubject: "Bookkeeping-Assistant",
    salary: 43649,
    netChange: -2,
    netChangeOneYear: 1900,
    netChangeThreeYear: 3800,
    netChangeFiveYear: 5200,
    liquidity: 280,
    averageTenureInYears: 2.9,
    growthRate: 0.015,
  },
  {
    jobTitle: "Budget Counselor",
    jobSubject: "Budget-Counselor",
    salary: 95109,
    netChange: -3,
    netChangeOneYear: 4200,
    netChangeThreeYear: 8800,
    netChangeFiveYear: 13500,
    liquidity: 570,
    averageTenureInYears: 3.6,
    growthRate: 0.026,
  },
  {
    jobTitle: "Claims Adjuster",
    jobSubject: "Claims-Adjuster",
    salary: 82272,
    netChange: -4,
    netChangeOneYear: 3600,
    netChangeThreeYear: 7200,
    netChangeFiveYear: 10800,
    liquidity: 510,
    averageTenureInYears: 3.3,
    growthRate: 0.021,
  },
  {
    jobTitle: "Corporate Controller",
    jobSubject: "Corporate-Controller",
    salary: 219288,
    netChange: 9,
    netChangeOneYear: 9800,
    netChangeThreeYear: 18500,
    netChangeFiveYear: 28000,
    liquidity: 1250,
    averageTenureInYears: 5.8,
    growthRate: 0.048,
  },
  {
    jobTitle: "Corporate Financial Analyst",
    jobSubject: "Corporate-Financial-Analyst",
    salary: 95439,
    netChange: -6,
    netChangeOneYear: 4200,
    netChangeThreeYear: 8600,
    netChangeFiveYear: 13200,
    liquidity: 570,
    averageTenureInYears: 3.4,
    growthRate: 0.025,
  },
  {
    jobTitle: "Credit Associate",
    jobSubject: "Credit-Associate",
    salary: 49118,
    netChange: -3,
    netChangeOneYear: 2200,
    netChangeThreeYear: 4500,
    netChangeFiveYear: 6400,
    liquidity: 310,
    averageTenureInYears: 2.8,
    growthRate: 0.017,
  },
  {
    jobTitle: "Economist",
    jobSubject: "Economist",
    salary: 58753,
    netChange: -2,
    netChangeOneYear: 2800,
    netChangeThreeYear: 5600,
    netChangeFiveYear: 8100,
    liquidity: 380,
    averageTenureInYears: 4.1,
    growthRate: 0.019,
  },
  {
    jobTitle: "Financial Advisor",
    jobSubject: "Financial-Advisor",
    salary: 112885,
    netChange: -7,
    netChangeOneYear: 5200,
    netChangeThreeYear: 10500,
    netChangeFiveYear: 16200,
    liquidity: 680,
    averageTenureInYears: 4.7,
    growthRate: 0.032,
  },
  {
    jobTitle: "Financial Intelligence Analyst",
    jobSubject: "Financial-Intelligence-Analyst",
    salary: 90274,
    netChange: 3,
    netChangeOneYear: 4000,
    netChangeThreeYear: 8200,
    netChangeFiveYear: 12800,
    liquidity: 550,
    averageTenureInYears: 3.5,
    growthRate: 0.024,
  },
  {
    jobTitle: "Financial Officer",
    jobSubject: "Financial-Officer",
    salary: 187036,
    netChange: -9,
    netChangeOneYear: 8500,
    netChangeThreeYear: 16800,
    netChangeFiveYear: 25500,
    liquidity: 1080,
    averageTenureInYears: 5.2,
    growthRate: 0.042,
  },
  {
    jobTitle: "Financial Planner",
    jobSubject: "Financial-Planner",
    salary: 118219,
    netChange: 5,
    netChangeOneYear: 5400,
    netChangeThreeYear: 11000,
    netChangeFiveYear: 17200,
    liquidity: 710,
    averageTenureInYears: 4.3,
    growthRate: 0.033,
  },
  {
    jobTitle: "Financial Planning Manager",
    jobSubject: "Financial-Planning-Manager",
    salary: 184461,
    netChange: 8,
    netChangeOneYear: 8300,
    netChangeThreeYear: 16500,
    netChangeFiveYear: 25000,
    liquidity: 1060,
    averageTenureInYears: 5.4,
    growthRate: 0.041,
  },
  {
    jobTitle: "Hedge Fund Trader",
    jobSubject: "Hedge-Fund-Trader",
    salary: 110800,
    netChange: -3,
    netChangeOneYear: 5000,
    netChangeThreeYear: 10200,
    netChangeFiveYear: 15800,
    liquidity: 670,
    averageTenureInYears: 3.9,
    growthRate: 0.031,
  },
  {
    jobTitle: "Insurance Analyst",
    jobSubject: "Insurance-Analyst",
    salary: 67201,
    netChange: -5,
    netChangeOneYear: 3200,
    netChangeThreeYear: 6400,
    netChangeFiveYear: 9200,
    liquidity: 420,
    averageTenureInYears: 3.2,
    growthRate: 0.02,
  },
  {
    jobTitle: "Investment Banker",
    jobSubject: "Investment-Banker",
    salary: 142821,
    netChange: -4,
    netChangeOneYear: 6500,
    netChangeThreeYear: 13200,
    netChangeFiveYear: 20500,
    liquidity: 850,
    averageTenureInYears: 4.6,
    growthRate: 0.037,
  },
  {
    jobTitle: "Loan Officer",
    jobSubject: "Loan-Officer",
    salary: 82154,
    netChange: 4,
    netChangeOneYear: 3700,
    netChangeThreeYear: 7400,
    netChangeFiveYear: 11000,
    liquidity: 500,
    averageTenureInYears: 3.7,
    growthRate: 0.022,
  },
  {
    jobTitle: "Portfolio Manager",
    jobSubject: "Portfolio-Manager",
    salary: 220574,
    netChange: -10,
    netChangeOneYear: 10000,
    netChangeThreeYear: 19500,
    netChangeFiveYear: 29800,
    liquidity: 1280,
    averageTenureInYears: 6.1,
    growthRate: 0.049,
  },
  {
    jobTitle: "Private Equity Associate",
    jobSubject: "Private-Equity-Associate",
    salary: 105935,
    netChange: 7,
    netChangeOneYear: 4800,
    netChangeThreeYear: 9800,
    netChangeFiveYear: 15200,
    liquidity: 640,
    averageTenureInYears: 4.0,
    growthRate: 0.03,
  },
  {
    jobTitle: "Quantitative Analyst",
    jobSubject: "Quantitative-Analyst",
    salary: 107776,
    netChange: 5,
    netChangeOneYear: 4900,
    netChangeThreeYear: 10000,
    netChangeFiveYear: 15500,
    liquidity: 650,
    averageTenureInYears: 4.2,
    growthRate: 0.029,
  },
  {
    jobTitle: "Risk Manager",
    jobSubject: "Risk-Manager",
    salary: 166751,
    netChange: -8,
    netChangeOneYear: 7600,
    netChangeThreeYear: 15200,
    netChangeFiveYear: 23000,
    liquidity: 970,
    averageTenureInYears: 5.0,
    growthRate: 0.039,
  },
  {
    jobTitle: "Tax Preparer",
    jobSubject: "Tax-Preparer",
    salary: 58928,
    netChange: 6,
    netChangeOneYear: 2700,
    netChangeThreeYear: 5500,
    netChangeFiveYear: 8000,
    liquidity: 370,
    averageTenureInYears: 3.0,
    growthRate: 0.018,
  },
];

// Mock related careers map (each position has ~10 similar positions)
export const MOCK_RELATED_CAREERS: Record<string, string[]> = {
  "Accountant-Supervisor": [
    "Corporate-Controller",
    "Financial-Officer",
    "Budget-Counselor",
    "Corporate-Financial-Analyst",
    "Financial-Planning-Manager",
    "Auditor-Appraiser",
    "Tax-Preparer",
    "Bookkeeping-Assistant",
    "Claims-Adjuster",
    "Financial-Planner",
  ],
  "Actuary-Clerk": [
    "Bookkeeping-Assistant",
    "Credit-Associate",
    "Tax-Preparer",
    "Insurance-Analyst",
    "Claims-Adjuster",
    "Auditor-Appraiser",
    "Budget-Counselor",
    "Economist",
    "Loan-Officer",
    "Financial-Intelligence-Analyst",
  ],
  "Auditor-Appraiser": [
    "Accountant-Supervisor",
    "Budget-Counselor",
    "Corporate-Financial-Analyst",
    "Financial-Intelligence-Analyst",
    "Risk-Manager",
    "Claims-Adjuster",
    "Tax-Preparer",
    "Insurance-Analyst",
    "Bookkeeping-Assistant",
    "Corporate-Controller",
  ],
  Banker: [
    "Loan-Officer",
    "Financial-Advisor",
    "Investment-Banker",
    "Credit-Associate",
    "Financial-Planner",
    "Portfolio-Manager",
    "Private-Equity-Associate",
    "Hedge-Fund-Trader",
    "Budget-Counselor",
    "Corporate-Controller",
  ],
  "Bookkeeping-Assistant": [
    "Actuary-Clerk",
    "Credit-Associate",
    "Tax-Preparer",
    "Accountant-Supervisor",
    "Budget-Counselor",
    "Claims-Adjuster",
    "Auditor-Appraiser",
    "Loan-Officer",
    "Insurance-Analyst",
    "Economist",
  ],
  "Budget-Counselor": [
    "Accountant-Supervisor",
    "Corporate-Financial-Analyst",
    "Financial-Planning-Manager",
    "Financial-Planner",
    "Corporate-Controller",
    "Auditor-Appraiser",
    "Financial-Officer",
    "Risk-Manager",
    "Investment-Banker",
    "Portfolio-Manager",
  ],
  "Claims-Adjuster": [
    "Insurance-Analyst",
    "Auditor-Appraiser",
    "Risk-Manager",
    "Budget-Counselor",
    "Accountant-Supervisor",
    "Corporate-Financial-Analyst",
    "Tax-Preparer",
    "Bookkeeping-Assistant",
    "Financial-Intelligence-Analyst",
    "Loan-Officer",
  ],
  "Corporate-Controller": [
    "Financial-Officer",
    "Corporate-Financial-Analyst",
    "Accountant-Supervisor",
    "Financial-Planning-Manager",
    "Portfolio-Manager",
    "Risk-Manager",
    "Budget-Counselor",
    "Investment-Banker",
    "Private-Equity-Associate",
    "Quantitative-Analyst",
  ],
  "Corporate-Financial-Analyst": [
    "Financial-Intelligence-Analyst",
    "Budget-Counselor",
    "Corporate-Controller",
    "Quantitative-Analyst",
    "Risk-Manager",
    "Accountant-Supervisor",
    "Financial-Planning-Manager",
    "Investment-Banker",
    "Portfolio-Manager",
    "Economist",
  ],
  "Credit-Associate": [
    "Loan-Officer",
    "Banker",
    "Actuary-Clerk",
    "Bookkeeping-Assistant",
    "Insurance-Analyst",
    "Tax-Preparer",
    "Claims-Adjuster",
    "Budget-Counselor",
    "Financial-Advisor",
    "Risk-Manager",
  ],
  Economist: [
    "Corporate-Financial-Analyst",
    "Financial-Intelligence-Analyst",
    "Quantitative-Analyst",
    "Risk-Manager",
    "Budget-Counselor",
    "Financial-Planning-Manager",
    "Portfolio-Manager",
    "Investment-Banker",
    "Auditor-Appraiser",
    "Corporate-Controller",
  ],
  "Financial-Advisor": [
    "Financial-Planner",
    "Investment-Banker",
    "Private-Equity-Associate",
    "Banker",
    "Portfolio-Manager",
    "Hedge-Fund-Trader",
    "Financial-Planning-Manager",
    "Corporate-Controller",
    "Loan-Officer",
    "Budget-Counselor",
  ],
  "Financial-Intelligence-Analyst": [
    "Corporate-Financial-Analyst",
    "Quantitative-Analyst",
    "Risk-Manager",
    "Financial-Officer",
    "Economist",
    "Budget-Counselor",
    "Auditor-Appraiser",
    "Insurance-Analyst",
    "Portfolio-Manager",
    "Investment-Banker",
  ],
  "Financial-Officer": [
    "Corporate-Controller",
    "Financial-Planning-Manager",
    "Portfolio-Manager",
    "Risk-Manager",
    "Accountant-Supervisor",
    "Investment-Banker",
    "Private-Equity-Associate",
    "Budget-Counselor",
    "Financial-Intelligence-Analyst",
    "Quantitative-Analyst",
  ],
  "Financial-Planner": [
    "Financial-Advisor",
    "Investment-Banker",
    "Financial-Planning-Manager",
    "Budget-Counselor",
    "Portfolio-Manager",
    "Private-Equity-Associate",
    "Corporate-Controller",
    "Banker",
    "Hedge-Fund-Trader",
    "Risk-Manager",
  ],
  "Financial-Planning-Manager": [
    "Financial-Officer",
    "Corporate-Controller",
    "Portfolio-Manager",
    "Financial-Planner",
    "Budget-Counselor",
    "Investment-Banker",
    "Risk-Manager",
    "Corporate-Financial-Analyst",
    "Private-Equity-Associate",
    "Accountant-Supervisor",
  ],
  "Hedge-Fund-Trader": [
    "Portfolio-Manager",
    "Quantitative-Analyst",
    "Investment-Banker",
    "Private-Equity-Associate",
    "Financial-Advisor",
    "Risk-Manager",
    "Financial-Planner",
    "Corporate-Controller",
    "Financial-Planning-Manager",
    "Banker",
  ],
  "Insurance-Analyst": [
    "Claims-Adjuster",
    "Risk-Manager",
    "Auditor-Appraiser",
    "Financial-Intelligence-Analyst",
    "Actuary-Clerk",
    "Credit-Associate",
    "Budget-Counselor",
    "Economist",
    "Quantitative-Analyst",
    "Corporate-Financial-Analyst",
  ],
  "Investment-Banker": [
    "Financial-Advisor",
    "Private-Equity-Associate",
    "Portfolio-Manager",
    "Hedge-Fund-Trader",
    "Financial-Planner",
    "Corporate-Controller",
    "Financial-Planning-Manager",
    "Banker",
    "Financial-Officer",
    "Quantitative-Analyst",
  ],
  "Loan-Officer": [
    "Banker",
    "Credit-Associate",
    "Financial-Advisor",
    "Insurance-Analyst",
    "Budget-Counselor",
    "Claims-Adjuster",
    "Bookkeeping-Assistant",
    "Tax-Preparer",
    "Risk-Manager",
    "Auditor-Appraiser",
  ],
  "Portfolio-Manager": [
    "Investment-Banker",
    "Hedge-Fund-Trader",
    "Financial-Planning-Manager",
    "Financial-Officer",
    "Corporate-Controller",
    "Private-Equity-Associate",
    "Quantitative-Analyst",
    "Risk-Manager",
    "Financial-Advisor",
    "Budget-Counselor",
  ],
  "Private-Equity-Associate": [
    "Investment-Banker",
    "Financial-Advisor",
    "Portfolio-Manager",
    "Hedge-Fund-Trader",
    "Financial-Planning-Manager",
    "Corporate-Controller",
    "Financial-Officer",
    "Quantitative-Analyst",
    "Risk-Manager",
    "Financial-Planner",
  ],
  "Quantitative-Analyst": [
    "Risk-Manager",
    "Hedge-Fund-Trader",
    "Financial-Intelligence-Analyst",
    "Corporate-Financial-Analyst",
    "Portfolio-Manager",
    "Investment-Banker",
    "Economist",
    "Financial-Officer",
    "Corporate-Controller",
    "Private-Equity-Associate",
  ],
  "Risk-Manager": [
    "Financial-Officer",
    "Corporate-Controller",
    "Quantitative-Analyst",
    "Financial-Intelligence-Analyst",
    "Portfolio-Manager",
    "Insurance-Analyst",
    "Corporate-Financial-Analyst",
    "Budget-Counselor",
    "Investment-Banker",
    "Hedge-Fund-Trader",
  ],
  "Tax-Preparer": [
    "Accountant-Supervisor",
    "Bookkeeping-Assistant",
    "Auditor-Appraiser",
    "Budget-Counselor",
    "Actuary-Clerk",
    "Claims-Adjuster",
    "Credit-Associate",
    "Insurance-Analyst",
    "Loan-Officer",
    "Corporate-Financial-Analyst",
  ],
};

// Create mock watchlist entries
const createWatchlistEntries: () => WatchlistJob[] = () => {
  return MOCK_JOB_SALARIES.map((job) => ({
    jobTitle: job.jobTitle,
    jobSubject: job.jobSubject,
    jobTitleAliases: [],
  }));
};

// Tom's Picks watchlist (featured positions from screenshot)
const TOMS_PICKS_JOBS = [
  "Accountant-Supervisor",
  "Actuary-Clerk",
  "Auditor-Appraiser",
  "Banker",
  "Bookkeeping-Assistant",
  "Budget-Counselor",
  "Claims-Adjuster",
  "Corporate-Controller",
  "Corporate-Financial-Analyst",
  "Credit-Associate",
  "Economist",
  "Financial-Advisor",
  "Financial-Intelligence-Analyst",
  "Financial-Officer",
  "Financial-Planner",
  "Financial-Planning-Manager",
  "Hedge-Fund-Trader",
  "Insurance-Analyst",
  "Investment-Banker",
  "Loan-Officer",
  "Portfolio-Manager",
  "Private-Equity-Associate",
  "Quantitative-Analyst",
  "Risk-Manager",
  "Tax-Preparer",
];

// Mock Watchlist DTOs
export const MOCK_WATCHLISTS: WatchlistDto[] = [
  {
    id: "toms-picks",
    title: "⭐️ Tom's Picks",
    groupName: "🐾 LossDog",
    entries: TOMS_PICKS_JOBS.map((subject) => {
      const job = MOCK_JOB_SALARIES.find((j) => j.jobSubject === subject);
      return {
        jobTitle: job?.jobTitle || subject,
        jobSubject: subject,
        jobTitleAliases: [],
      };
    }),
  },
  {
    id: "finance-banking",
    title: "Banking & Finance",
    groupName: "Finance",
    entries: ["Banker", "Investment-Banker", "Loan-Officer", "Financial-Advisor", "Financial-Planner"].map(
      (subject) => {
        const job = MOCK_JOB_SALARIES.find((j) => j.jobSubject === subject);
        return {
          jobTitle: job?.jobTitle || subject,
          jobSubject: subject,
          jobTitleAliases: [],
        };
      },
    ),
  },
  {
    id: "finance-analysis",
    title: "Financial Analysis",
    groupName: "Finance",
    entries: ["Corporate-Financial-Analyst", "Financial-Intelligence-Analyst", "Quantitative-Analyst"].map(
      (subject) => {
        const job = MOCK_JOB_SALARIES.find((j) => j.jobSubject === subject);
        return {
          jobTitle: job?.jobTitle || subject,
          jobSubject: subject,
          jobTitleAliases: [],
        };
      },
    ),
  },
  {
    id: "accounting",
    title: "Accounting",
    groupName: "Accounting",
    entries: ["Accountant-Supervisor", "Bookkeeping-Assistant", "Auditor-Appraiser", "Tax-Preparer"].map((subject) => {
      const job = MOCK_JOB_SALARIES.find((j) => j.jobSubject === subject);
      return {
        jobTitle: job?.jobTitle || subject,
        jobSubject: subject,
        jobTitleAliases: [],
      };
    }),
  },
];

export const MOCK_ALL_WATCHLIST_JOBS = createWatchlistEntries();

// Mock Watchlist Groups
export const MOCK_WATCHLIST_GROUPS: WatchlistGroup[] = [
  {
    id: "LossDog",
    title: "🐾 LossDog",
    watchlists: [MOCK_WATCHLISTS[0]], // Tom's Picks
  },
  {
    id: "Finance",
    title: "Finance",
    watchlists: [MOCK_WATCHLISTS[1], MOCK_WATCHLISTS[2]], // Banking & Finance, Financial Analysis
  },
  {
    id: "Accounting",
    title: "Accounting",
    watchlists: [MOCK_WATCHLISTS[3]], // Accounting
  },
  {
    id: "All",
    title: "All",
    watchlists: [
      {
        id: "All",
        title: "All",
        groupName: "All",
        entries: MOCK_ALL_WATCHLIST_JOBS,
      },
    ],
  },
];

/**
 * Helper function to create a mock JobSalary instance
 * Note: Does NOT initialize the subscription function (for mock, we handle updates differently)
 */
export function createMockJobSalary(data: MockJobSalaryData, metroArea: MetroArea, quantile: Quantile): JobSalary {
  const jobSalary = new JobSalary(
    data.jobSubject,
    `job-salary.${data.jobSubject}.${metroArea.id}.${quantile.id}`,
    data.jobTitle,
    [],
    metroArea,
    quantile,
    null, // No subscription function for mock
  );

  // Initialize signal values
  jobSalary.salary.value = data.salary;
  jobSalary.salaryUp.value = data.netChange > 0;
  jobSalary.salaryDown.value = data.netChange < 0;

  jobSalary.netChange.value = data.netChange;
  jobSalary.netChangeUp.value = data.netChange > 0;
  jobSalary.netChangeDown.value = data.netChange < 0;

  jobSalary.netChangeOneYear.value = data.netChangeOneYear;
  jobSalary.netChangeThreeYear.value = data.netChangeThreeYear;
  jobSalary.netChangeFiveYear.value = data.netChangeFiveYear;

  jobSalary.liquidity.value = data.liquidity;
  jobSalary.averageTenureInYears.value = data.averageTenureInYears;

  jobSalary.growthRate.value = data.growthRate;
  jobSalary.growthRateUp.value = data.growthRate > 0;
  jobSalary.growthRateDown.value = data.growthRate < 0;

  // Calculate projection values (simple estimates)
  jobSalary.totalCostProjectionOneYear.value = data.salary * 1.03;
  jobSalary.totalCostProjectionThreeYears.value = data.salary * 1.09;
  jobSalary.totalCostProjectionFiveYears.value = data.salary * 1.15;

  return jobSalary;
}
